import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { datadogRum } from "@datadog/browser-rum";
import { Icon } from "semantic-ui-react";

import { useBooleanState, useDelay } from "hooks";

import {
  SnoozeOptions,
  UpgradeButton,
  WorkspaceBanner,
} from "../Workspace/components/Insights/components/InsightsBanner/components";

export function InAppBanner({
  title,
  message,
  visible,
  learnMore,
  ctaButton,
  expiry,
  onDismiss,
  ...props
}) {
  return (
    <WorkspaceBanner
      visible={visible}
      title={title}
      action={
        ctaButton && (
          <UpgradeButton
            data-dd-action-name={`Banner action for ${title}`}
            onClick={onDismiss}
            inverted
            primary={false}
            buttonData={ctaButton}
          />
        )
      }
      dismissComponent={
        <SnoozeOptions expirationDate={expiry} onSnooze={onDismiss} />
      }
      {...props}
    >
      <span style={{ marginRight: "5px" }}>{message}</span>
      {learnMore && (
        <a
          href={learnMore.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "white" }}
        >
          <span
            data-test="learn-more-text"
            style={{ textDecoration: "underline" }}
          >
            {learnMore.text}
          </span>
          <Icon
            data-test="learn-more-icon"
            name="external"
            size="small"
            style={{ marginLeft: "5px" }}
          />
        </a>
      )}
    </WorkspaceBanner>
  );
}

InAppBanner.propTypes = {
  visible: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  learnMore: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.string,
  }),
  ctaButton: PropTypes.object,
  expiry: PropTypes.instanceOf(Date),
  onDismiss: PropTypes.func.isRequired,
};

InAppBanner.defaultProps = {
  visible: false,
  title: "",
  message: "",
  expiry: undefined,
  learnMore: undefined,
  ctaButton: undefined,
};

function InAppBannerWrapper({
  renderDelay,
  visible,
  forceOnTop,
  onDismiss,
  title,
  ...otherProps
}) {
  const [shouldRender, startRender, stopRender] = useBooleanState(false);
  const renderWithDelay = useDelay(renderDelay);

  useEffect(() => {
    if (visible && !shouldRender) {
      renderWithDelay(() => {
        startRender();
        if (title) datadogRum.addAction(`Banner ${title} is rendered`);
      });
    }
  }, [title, visible, shouldRender, renderWithDelay, startRender, stopRender]);

  return (
    <React.Fragment>
      {forceOnTop && (
        <InAppBanner
          {...otherProps}
          title={title}
          style={{ position: "absolute", zIndex: "9999", width: "100%" }}
          visible={shouldRender}
          onDismiss={(args) => {
            stopRender();
            onDismiss(args);
          }}
        />
      )}
      <InAppBanner
        {...otherProps}
        title={title}
        visible={shouldRender}
        onDismiss={(args) => {
          stopRender();
          onDismiss(args);
        }}
      />
    </React.Fragment>
  );
}

InAppBannerWrapper.propTypes = {
  title: PropTypes.string,
  visible: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  renderDelay: PropTypes.number,
  forceOnTop: PropTypes.bool,
};

InAppBannerWrapper.defaultProps = {
  title: "",
  visible: false,
  renderDelay: 0,
  forceOnTop: false,
};

export default InAppBannerWrapper;
